import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Paper, Typography, Chip, Stack } from '@mui/material';
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { loadPortalData } from '../slices/portalSlice';
import { severityChipSx, severityLabel, formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';

export default function PortalOverviewPage() {
  const dispatch = useDispatch();
  const { overview, vulnerabilities, notifications } = useSelector((s) => s.portal);

  useEffect(() => { dispatch(loadPortalData()); }, [dispatch]);

  const openVulns = vulnerabilities.filter((v) => v.status !== 'resolved');
  const unread = notifications.filter((n) => !n.read);

  return (
    <Box>
      <PageHeader
        title={overview ? `Welcome back, ${overview.orgName}` : 'Welcome'}
        subtitle="A snapshot of your website and infrastructure security"
      />

      {overview && (
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Open Tickets" value={overview.openTickets} accent={tokens.severity.medium} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Open Incidents" value={overview.openIncidents} icon={BugReportRoundedIcon} accent={tokens.severity.high} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Subscription Plan" value={overview.subscriptionPlan} accent={tokens.brand.main} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Subscription Status" value={overview.subscriptionStatus} accent={tokens.severity.low} />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Open Findings</Typography>
              <Chip label={`${openVulns.length} open`} size="small" sx={{ backgroundColor: tokens.bg.elevated, color: tokens.text.secondary }} />
            </Box>
            <Stack spacing={1.2}>
              {openVulns.slice(0, 4).map((v) => (
                <Box key={v.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.2, borderRadius: 1.5, border: `1px solid ${tokens.border}` }}>
                  <Typography sx={{ fontSize: '0.8rem', color: tokens.text.primary }}>{v.title}</Typography>
                  <Chip label={severityLabel(v.severity)} size="small" sx={{ ...severityChipSx(v.severity), flexShrink: 0, ml: 1 }} />
                </Box>
              ))}
              {openVulns.length === 0 && (
                <Typography variant="body2" sx={{ color: tokens.text.muted }}>No open findings — nice work.</Typography>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Recent Notifications</Typography>
              {unread.length > 0 && <Chip label={`${unread.length} new`} size="small" sx={{ color: tokens.brand.main, border: `1px solid ${tokens.brand.main}`, backgroundColor: tokens.brand.glow }} />}
            </Box>
            <Stack spacing={1.2}>
              {notifications.slice(0, 4).map((n) => (
                <Box key={n.id} sx={{ p: 1.2, borderRadius: 1.5, border: `1px solid ${tokens.border}`, backgroundColor: n.read ? 'transparent' : tokens.bg.elevated }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: tokens.text.primary, fontWeight: n.read ? 400 : 600 }}>{n.title}</Typography>
                    <Chip label={severityLabel(n.severity)} size="small" sx={{ ...severityChipSx(n.severity), flexShrink: 0, ml: 1 }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: tokens.text.muted }}>{formatRelativeTime(n.timestamp)}</Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
